/** Complete, role-aware FlahaSOIL navigation library. */
import {
	Box,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Typography,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import BusinessIcon from "@mui/icons-material/Business";
import DashboardIcon from "@mui/icons-material/Dashboard";
import FolderIcon from "@mui/icons-material/Folder";
import GroupIcon from "@mui/icons-material/Group";
import HomeIcon from "@mui/icons-material/Home";
import IosShareIcon from "@mui/icons-material/IosShare";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PersonIcon from "@mui/icons-material/Person";
import SellIcon from "@mui/icons-material/Sell";
import ScienceIcon from "@mui/icons-material/Science";
import { OrganizationRole } from "@flaha/shared-types";
import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";

import { useAuth } from "../../auth";
import { reducedMotionMediaQuery } from "../../theme/motionTokens";

interface NavItem {
	label: string;
	to: string;
	icon: ReactNode;
	exact?: boolean;
	requiresAuth?: boolean;
	requiresOrgAdmin?: boolean;
	requiresOrgOwner?: boolean;
}

interface NavSection {
	id: string;
	label: string;
	items: NavItem[];
}

const SECTIONS: NavSection[] = [
	{
		id: "workspace",
		label: "Workspace",
		items: [
			{ label: "Home", to: "/", icon: <HomeIcon />, exact: true },
			{ label: "Dashboard", to: "/dashboard", icon: <DashboardIcon />, requiresAuth: true },
			{ label: "Projects", to: "/projects", icon: <FolderIcon />, requiresAuth: true },
			{ label: "New Soil Analysis", to: "/soil-tests/new", icon: <ScienceIcon />, requiresAuth: true },
			{ label: "Reports", to: "/reports", icon: <AssessmentIcon />, requiresAuth: true },
			{ label: "Scientific Standards", to: "/standards", icon: <LibraryBooksIcon /> },
			{ label: "Pricing", to: "/pricing", icon: <SellIcon /> },
		],
	},
	{
		id: "integration",
		label: "Integration",
		items: [
			{
				label: "FlahaCalc Export",
				to: "/flahacalc-export",
				icon: <IosShareIcon />,
				requiresAuth: true,
			},
		],
	},
	{
		id: "organization",
		label: "Organization administration",
		items: [
			{
				label: "Organization Settings",
				to: "/organization/settings",
				icon: <BusinessIcon />,
				requiresAuth: true,
				requiresOrgAdmin: true,
			},
			{
				label: "Members",
				to: "/organization/members",
				icon: <GroupIcon />,
				requiresAuth: true,
				requiresOrgAdmin: true,
			},
			{
				label: "Invitations",
				to: "/organization/invitations",
				icon: <MailOutlineIcon />,
				requiresAuth: true,
				requiresOrgAdmin: true,
			},
		],
	},
	{
		id: "account",
		label: "Account",
		items: [
			{
				label: "Account",
				to: "/account",
				icon: <PersonIcon />,
				requiresAuth: true,
			},
		],
	},
];

interface SidebarNavProps {
	onNavigate?: () => void;
}

export function SidebarNav({ onNavigate }: SidebarNavProps) {
	const { status, activeOrganization, memberships } = useAuth();
	const isAuthed = status === "authenticated";
	const activeRole = activeOrganization
		? memberships.find(
				(membership) => membership.organizationId === activeOrganization.id
			)?.role
		: undefined;
	const isOrgOwner = activeRole === OrganizationRole.OWNER;
	const isOrgAdmin = isOrgOwner || activeRole === OrganizationRole.ADMIN;

	const visibleSections = SECTIONS.map((section) => ({
		...section,
		items: section.items.filter((item) => {
			if (item.requiresAuth && !isAuthed) return false;
			if (item.requiresOrgAdmin && !isOrgAdmin) return false;
			if (item.requiresOrgOwner && !isOrgOwner) return false;
			return true;
		}),
	})).filter((section) => section.items.length > 0);

	return (
		<Box
			component="nav"
			aria-label="Complete application navigation"
			sx={{ py: 1.75, overflowY: "auto", flexGrow: 1 }}
		>
			<Box sx={{ px: 2, pb: 1.5 }}>
				<Typography
					variant="caption"
					sx={{ color: "workspace.textMuted", lineHeight: 1.4 }}
				>
					Complete navigation and administration
				</Typography>
			</Box>

			{visibleSections.map((section) => (
				<Box key={section.id} sx={{ mb: 1.5 }}>
					<Typography
						variant="overline"
						sx={{
							px: 2,
							color: "workspace.textMuted",
							fontSize: 10,
							fontWeight: 700,
							letterSpacing: "0.1em",
						}}
					>
						{section.label}
					</Typography>
					<List dense disablePadding>
						{section.items.map((item) => (
							<ListItemButton
								key={item.to}
								component={NavLink}
								to={item.to}
								end={item.exact ?? false}
								onClick={onNavigate}
								sx={(theme) => ({
									mx: 1,
									my: 0.25,
									minHeight: 42,
									borderRadius: 1.5,
									color: "workspace.textSecondary",
									transition: theme.motion.transition.surface,
									"&.active": {
										color: "workspace.textPrimary",
										backgroundColor: "rgba(119,169,87,0.16)",
										boxShadow: `inset 3px 0 0 ${theme.palette.workspace.green}`,
										"& .MuiListItemIcon-root": { color: "workspace.green" },
									},
									"&:hover": {
										color: "workspace.textPrimary",
										backgroundColor: "rgba(246,240,227,0.07)",
									},
									[reducedMotionMediaQuery]: { transition: "none" },
								})}
							>
								<ListItemIcon sx={{ minWidth: 38, color: "inherit" }}>
									{item.icon}
								</ListItemIcon>
								<ListItemText
									primary={item.label}
									primaryTypographyProps={{ fontSize: 13.5, fontWeight: 600 }}
								/>
							</ListItemButton>
						))}
					</List>
				</Box>
			))}
		</Box>
	);
}
